/* schema.org Course payload builder for Academy program detail pages.
 *
 * Google has a dedicated "Course experience" SERP module (a horizontal
 * carousel of courses tied to an entity query like "data analytics
 * courses near me"). Eligibility requires `Course` + at least one
 * `hasCourseInstance` with `courseMode` + duration. We emit both the
 * required Course node and a CourseInstance child so each program
 * qualifies on every detail page automatically.
 *
 * Reuses getOrganizationSchema() so the `provider` reference is
 * byte-identical to the Org schema injected on Home + Copyright. */
import { getOrganizationSchema } from './orgSchema';

/* Convert friendly duration strings ("12 weeks", "12–24 weeks", "Self-paced",
 * "Rolling (term-based)") into ISO 8601 durations Google accepts. Falls back
 * to passing the raw string in `courseWorkload` (which is also a string field
 * per Google's spec) when no parse is possible. */
function parseDurationToIso8601(duration) {
  if (!duration || typeof duration !== 'string') return null;
  // Match "12 weeks", "12-24 weeks", "12–24 weeks" — pick the upper bound.
  const weeksMatch = duration.match(/(\d+)\s*[\u2013\u2014\-]\s*(\d+)\s*weeks?/i)
                  || duration.match(/(\d+)\s*weeks?/i);
  if (weeksMatch) {
    const upper = weeksMatch[2] || weeksMatch[1];
    return `P${upper}W`;
  }
  const hoursMatch = duration.match(/(\d+)\s*hours?/i);
  if (hoursMatch) return `PT${hoursMatch[1]}H`;
  const daysMatch = duration.match(/(\d+)\s*days?/i);
  if (daysMatch) return `P${daysMatch[1]}D`;
  return null;
}

export function getCourseSchema(program, origin) {
  const safeOrigin = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://zhelta.com');
  const url = `${safeOrigin}/academy/programs/${program.slug}`;
  const isoDuration = parseDurationToIso8601(program.duration);

  // Provider points back to the canonical Organization @id so Google joins
  // the entities. The "provider" itself is the same Org node used elsewhere.
  const provider = getOrganizationSchema(safeOrigin);

  const courseInstance = {
    '@type': 'CourseInstance',
    courseMode: 'Online',
    // Workload string is always safe to populate; ISO duration is set when
    // parseable so Google can render the duration chip in the carousel.
    courseWorkload: program.duration || 'Self-paced',
    location: {
      '@type': 'VirtualLocation',
      url,
    },
    inLanguage: 'en',
    instructor: {
      '@type': 'Organization',
      '@id': `${safeOrigin}/#organization`,
      name: 'ZHELTA Academy Faculty',
    },
  };
  if (isoDuration) courseInstance.courseWorkloadDuration = isoDuration;

  const course = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': `${url}#course`,
    name: program.title || program.name,
    description: program.summary || program.tagline || '',
    url,
    image: program.image || `${safeOrigin}/og-images/academy.svg`,
    provider: {
      '@type': 'Organization',
      '@id': `${safeOrigin}/#organization`,
      name: provider.name,
      url: provider.url,
      sameAs: provider.sameAs,
    },
    educationalLevel: program.level || 'All levels',
    audience: {
      '@type': 'EducationalAudience',
      educationalRole: program.audience || 'Aspiring learners',
    },
    timeRequired: isoDuration || undefined,
    hasCourseInstance: courseInstance,
    inLanguage: 'en',
  };

  // Strip any explicitly undefined keys so the JSON stays clean.
  Object.keys(course).forEach((k) => course[k] === undefined && delete course[k]);
  return course;
}
